export type PortalSortDirection = "ascending" | "descending";

function compareMissingValues(
  leftMissing: boolean,
  rightMissing: boolean,
): number | null {
  if (!leftMissing && !rightMissing) return null;
  if (leftMissing && rightMissing) return 0;
  return leftMissing ? 1 : -1;
}

function applyDirection(
  comparison: number,
  direction: PortalSortDirection,
): number {
  return direction === "ascending" ? comparison : -comparison;
}

export function comparePortalText(
  left: string | null | undefined,
  right: string | null | undefined,
  direction: PortalSortDirection,
): number {
  const normalizedLeft = (left ?? "").replace(/\s+/g, " ").trim();
  const normalizedRight = (right ?? "").replace(/\s+/g, " ").trim();
  const missingComparison = compareMissingValues(
    normalizedLeft.length === 0,
    normalizedRight.length === 0,
  );
  if (missingComparison !== null) return missingComparison;

  return applyDirection(
    normalizedLeft.localeCompare(normalizedRight, undefined, {
      numeric: true,
      sensitivity: "base",
    }),
    direction,
  );
}

export function comparePortalNumber(
  left: number | null | undefined,
  right: number | null | undefined,
  direction: PortalSortDirection,
): number {
  const leftMissing = left === null || left === undefined || !Number.isFinite(left);
  const rightMissing =
    right === null || right === undefined || !Number.isFinite(right);
  const missingComparison = compareMissingValues(leftMissing, rightMissing);
  if (missingComparison !== null) return missingComparison;

  return applyDirection((left as number) - (right as number), direction);
}

export function comparePortalDate(
  left: string | null | undefined,
  right: string | null | undefined,
  direction: PortalSortDirection,
): number {
  const leftTime = left ? new Date(left).getTime() : Number.NaN;
  const rightTime = right ? new Date(right).getTime() : Number.NaN;
  return comparePortalNumber(
    Number.isNaN(leftTime) ? null : leftTime,
    Number.isNaN(rightTime) ? null : rightTime,
    direction,
  );
}

export function stablePortalSort<TRecord>(
  records: readonly TRecord[],
  compare: (left: TRecord, right: TRecord) => number,
): TRecord[] {
  return records
    .map((record, originalIndex) => ({ record, originalIndex }))
    .sort(
      (left, right) =>
        compare(left.record, right.record) ||
        left.originalIndex - right.originalIndex,
    )
    .map(({ record }) => record);
}
